import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useGetUnsubscribeTargetQuery, useUnsubscribeEmailMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { data, isLoading, isError } = useGetUnsubscribeTargetQuery(token, { skip: !token });
  const [unsubscribeEmail, { isLoading: isSaving }] = useUnsubscribeEmailMutation();
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const handleUnsubscribe = async () => {
    setFailure(null);
    try {
      await unsubscribeEmail(token).unwrap();
      setDone(true);
    } catch (error: unknown) {
      setFailure(apiErrorMessage(error, "We could not complete your request. Please try again."));
    }
  };

  const sender = data?.organisationName ?? "this business";

  if (!token || isError) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-foreground">
        <Card>
          <CardHeader>
            <CardTitle>This link is not valid</CardTitle>
            <CardDescription>
              The unsubscribe link has been changed or is incomplete. Reply to the email you received and ask to be
              removed, and the sender will take care of it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (done || data?.alreadyUnsubscribed) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-foreground">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              You have been unsubscribed
            </CardTitle>
            <CardDescription>
              {data?.emailAddress} will not receive any more marketing email from {sender}. You may still get replies
              to messages you send, and anything you specifically ask for.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-foreground">
      <Card>
        <CardHeader>
          <CardTitle>Unsubscribe from {sender}</CardTitle>
          <CardDescription>
            Confirm below and {data?.emailAddress} will be removed from {sender}'s email list.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {failure && <p className="text-sm text-destructive">{failure}</p>}
          <Button onClick={() => void handleUnsubscribe()} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Unsubscribe
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
